import React, { useState, useRef, useEffect } from "react";
import { Bot, Send, X, Sparkles } from "lucide-react";
import api from "@/lib/api";

export default function AICoachFloating() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your AI English coach. Ask anything — grammar, mock prep, vocab, or run a quick speaking warm-up." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const newMsgs = [...messages, { role: "user", content: input.trim() }];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { messages: newMsgs.slice(-12), mode: "coach" });
      setMessages([...newMsgs, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages([...newMsgs, { role: "assistant", content: "Couldn't reach the AI right now. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-coach-floating-btn"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#F77F00] hover:bg-[#D26900] flex items-center justify-center shadow-[0_12px_30px_-10px_rgba(225,29,72,0.55)] hover:scale-105 transition-all text-white"
        aria-label="AI Coach"
      >
        {open ? <X className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
      </button>

      {open && (
        <div data-testid="ai-coach-panel" className="fixed bottom-24 right-6 z-40 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] bg-white border border-slate-200 rounded-3xl flex flex-col overflow-hidden shadow-[0_30px_80px_-30px_rgba(11,45,92,0.35)]">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 surface-navy">
            <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-medium text-white">LingoQ Coach</div>
              <div className="text-xs text-white/70 flex items-center gap-1"><span className="pulse-dot" /> Online</div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar bg-[#F6F8FB]">
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] text-sm leading-relaxed ${
                m.role === "user"
                  ? "ml-auto bg-[#003049] text-white rounded-2xl rounded-br-md px-4 py-2.5"
                  : "bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-bl-md px-4 py-2.5"
              }`}>
                {m.content}
              </div>
            ))}
            {loading && <div className="text-xs text-slate-400 px-2">thinking...</div>}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-slate-100 bg-white flex gap-2">
            <input
              data-testid="ai-coach-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask anything..."
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#F77F00]"
            />
            <button
              onClick={send}
              data-testid="ai-coach-send"
              disabled={loading}
              className="w-10 h-10 rounded-xl bg-[#F77F00] hover:bg-[#D26900] disabled:opacity-50 flex items-center justify-center text-white"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
